import * as React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  InputAdornment,
  Stack,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  Chip,
  Skeleton,
  alpha,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import InventoryRoundedIcon from '@mui/icons-material/InventoryRounded';

// ─── Types ─────────────────────────────────────────────────────────────────────

type Order = 'asc' | 'desc';

export interface MaterialRow {
  id: number;
  vendorName: string;
  trade: string;
  manufacturer: string;
  materialName: string;
  itemCode: string;
  versionStartDate: string;
  versionEndDate: string;
  grade: string;
  unit: string;
  unitCost: number;
  inStock: boolean;
}

type SortableKey = keyof MaterialRow;

interface HeadCell {
  id: SortableKey;
  label: string;
  numeric: boolean;
  minWidth?: number;
}

interface MaterialTableProps {
  /** Inject your own data; defaults to empty. */
  initialRows?: MaterialRow[];
  /** Called when user clicks "Add New Material". */
  onAdd?: () => void;
  /** Called when user clicks the edit icon. */
  onEdit?: (row: MaterialRow) => void;
  /** Called when user confirms deletion; should return a Promise. */
  onDelete?: (id: number) => Promise<void>;
  /** Show skeleton loaders while true. */
  loading?: boolean;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const HEAD_CELLS: readonly HeadCell[] = [
  { id: 'vendorName',        label: 'Vendor Name',        numeric: false, minWidth: 140 },
  { id: 'trade',             label: 'Trade',              numeric: false, minWidth: 100 },
  { id: 'manufacturer',      label: 'Manufacturer',       numeric: false, minWidth: 120 },
  { id: 'materialName',      label: 'Material Name',      numeric: false, minWidth: 140 },
  { id: 'grade',             label: 'Grade',              numeric: false, minWidth: 90  },
  { id: 'itemCode',          label: 'Item Code',          numeric: false, minWidth: 110 },
  { id: 'unit',              label: 'Unit',               numeric: false, minWidth: 80  },
  { id: 'unitCost',          label: 'Unit Cost',          numeric: true,  minWidth: 100 },
  { id: 'versionStartDate',  label: 'Version Start',      numeric: false, minWidth: 130 },
  { id: 'versionEndDate',    label: 'Version End',        numeric: false, minWidth: 120 },
];

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

// ─── Utilities ─────────────────────────────────────────────────────────────────

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: Record<Key, any>, b: Record<Key, any>) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

// ─── Sub-components ────────────────────────────────────────────────────────────

interface EnhancedTableHeadProps {
  order: Order;
  orderBy: SortableKey;
  onRequestSort: (event: React.MouseEvent<unknown>, property: SortableKey) => void;
}

const EnhancedTableHead: React.FC<EnhancedTableHeadProps> = ({ order, orderBy, onRequestSort }) => {
  const createSortHandler = (property: SortableKey) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {HEAD_CELLS.map((cell) => (
          <TableCell
            key={cell.id}
            align={cell.numeric ? 'right' : 'left'}
            sortDirection={orderBy === cell.id ? order : false}
            sx={{
              minWidth: cell.minWidth,
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              backgroundColor: (theme) => theme.palette.grey[50],
              borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
              py: 1.5,
              whiteSpace: 'nowrap',
            }}
          >
            <TableSortLabel
              active={orderBy === cell.id}
              direction={orderBy === cell.id ? order : 'asc'}
              onClick={createSortHandler(cell.id)}
              sx={{
                '&.Mui-active': { color: 'primary.main' },
                '&:hover': { color: 'primary.main' },
              }}
            >
              {cell.label}
              {orderBy === cell.id && (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              )}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell
          align="center"
          sx={{
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            backgroundColor: (theme) => theme.palette.grey[50],
            borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
            py: 1.5,
          }}
        >
          Actions
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

const SkeletonRows: React.FC<{ count: number }> = ({ count }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <TableRow key={i}>
        {HEAD_CELLS.map((cell) => (
          <TableCell key={cell.id}>
            <Skeleton variant="text" width={cell.numeric ? 60 : '80%'} height={20} />
          </TableCell>
        ))}
        <TableCell align="center">
          <Stack direction="row" spacing={1} justifyContent="center">
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="circular" width={28} height={28} />
          </Stack>
        </TableCell>
      </TableRow>
    ))}
  </>
);

const EmptyState: React.FC<{ hasSearch: boolean }> = ({ hasSearch }) => (
  <TableRow>
    <TableCell colSpan={HEAD_CELLS.length + 1}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={8}
        gap={1.5}
      >
        <InventoryRoundedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
          {hasSearch ? 'No results found' : 'No materials yet'}
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center" maxWidth={320}>
          {hasSearch
            ? 'Try adjusting your search terms or clear the filter to see all materials.'
            : 'Get started by adding your first material to the inventory.'}
        </Typography>
      </Box>
    </TableCell>
  </TableRow>
);

// ─── Main Component ────────────────────────────────────────────────────────────

const MaterialTable: React.FC<MaterialTableProps> = ({
  initialRows = [],
  onAdd,
  onEdit,
  onDelete,
  loading = false,
}) => {
  const [rows, setRows] = React.useState<MaterialRow[]>(initialRows);
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<SortableKey>('vendorName');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [deleteTargetId, setDeleteTargetId] = React.useState<number | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Sync if parent updates initialRows
  React.useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const handleRequestSort = React.useCallback(
    (_event: React.MouseEvent<unknown>, property: SortableKey) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy],
  );

  const filteredRows = React.useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.vendorName?.toLowerCase().includes(q) ||
        r.materialName?.toLowerCase().includes(q) ||
        r.itemCode?.toLowerCase().includes(q) ||
        r.trade?.toLowerCase().includes(q) ||
        r.manufacturer?.toLowerCase().includes(q),
    );
  }, [rows, searchQuery]);

  const sortedRows = React.useMemo(
    () => [...filteredRows].sort(getComparator(order, orderBy)),
    [filteredRows, order, orderBy],
  );

  const visibleRows = React.useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const openDeleteDialog = (id: number) => setDeleteTargetId(id);
  const closeDeleteDialog = () => { if (!isDeleting) setDeleteTargetId(null); };

  const handleConfirmDelete = async () => {
    if (deleteTargetId === null) return;
    setIsDeleting(true);
    try {
      await onDelete?.(deleteTargetId);
      setRows((prev) => prev.filter((r) => r.id !== deleteTargetId));
      setSnackbar({ open: true, message: 'Material deleted successfully.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete material. Please try again.', severity: 'error' });
    } finally {
      setIsDeleting(false);
      setDeleteTargetId(null);
    }
  };

  return (
    <>
      {/* ── Table Card ── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {/* ── Toolbar ── */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          px={3}
          py={2.5}
          gap={2}
          sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1}>
              Materials
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {filteredRows.length} {filteredRows.length === 1 ? 'record' : 'records'}
              {searchQuery && ` matching "${searchQuery}"`}
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
            <TextField
              size="small"
              placeholder="Search by name, code, vendor…"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', sm: 280 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.875rem',
                },
              }}
            />
            <Button
              onClick={onAdd}
              variant="contained"
              disableElevation
              startIcon={<AddRoundedIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                whiteSpace: 'nowrap',
              }}
            >
              Add Material
            </Button>
          </Stack>
        </Box>

        {/* ── Table ── */}
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="medium">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {loading ? (
                <SkeletonRows count={rowsPerPage} />
              ) : visibleRows.length === 0 ? (
                <EmptyState hasSearch={!!searchQuery} />
              ) : (
                visibleRows.map((row, index) => (
                  <TableRow
                    hover
                    key={row.id}
                    sx={{
                      '&:last-child td': { border: 0 },
                      animationDelay: `${index * 30}ms`,
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {row.vendorName}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.trade}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem', height: 22, borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{row.manufacturer}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{row.materialName}</TableCell>
                    <TableCell>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          backgroundColor: (theme) => theme.palette.grey[100],
                          color: 'text.secondary',
                        }}
                      >
                        {row.itemCode}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      {row.versionStartDate}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      {row.versionEndDate}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.grade}
                        size="small"
                        color={
                          row.grade === 'Grade A' ? 'success'
                          : row.grade === 'Grade B' ? 'warning'
                          : 'default'
                        }
                        sx={{ fontSize: '0.72rem', height: 20, fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{row.unit}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(row.unitCost)}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit material" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onEdit?.(row)}
                            aria-label={`Edit ${row.materialName}`}
                            sx={{
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                              },
                            }}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete material" arrow>
                          <IconButton
                            size="small"
                            onClick={() => openDeleteDialog(row.id)}
                            aria-label={`Delete ${row.materialName}`}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                          >
                            <DeleteRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Pagination ── */}
        <TablePagination
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            fontSize: '0.8rem',
          }}
        />
      </Paper>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={deleteTargetId !== null}
        onClose={closeDeleteDialog}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            maxWidth: 380,
            width: '100%',
            border: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 1 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <ErrorOutlineRoundedIcon sx={{ fontSize: 32, color: 'error.main' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Delete Material?
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
            This material will be permanently removed from the inventory. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            onClick={closeDeleteDialog}
            disabled={isDeleting}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            variant="contained"
            color="error"
            disableElevation
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MaterialTable;